import React from 'react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import { type AnyAri } from '@atlassian/ari';
import {
	type SearchResultEntity,
	type SearchResultGraphDocument,
} from '@atlassian/dynamic-cards-search-client';

import { LastModified } from '../../../../../../../common/ui/last-modified';
import { RestrictedLozenge } from '../../../../../../../common/ui/restricted-lozenge';
import { SearchMetadata } from '../../../../../../../common/ui/search-metadata';
import { SearchResult } from '../../../../../../../common/ui/search-result';
import {
	enrichExcerpt,
	isNonPublic,
	processExcerpt,
} from '../../../../../../../common/utils/search-result-utils';
import { useBootstrap, usePrimaryProduct } from '../../../../../../../controllers/store';

const footerContainer = xcss({
	paddingTop: 'space.100',
});

const typeLabelContainer = xcss({
	'::first-letter': {
		textTransform: 'capitalize',
	},
});

interface GraphResultProps {
	partial: SearchResultGraphDocument<SearchResultEntity>;
	/** The position in the results list shown to the user. */
	position?: number;
	resultAri: AnyAri;
	icon: React.ReactNode;
	integrationKey?: string;
}

const getLabel = (entityNoun: string) => entityNoun.replace(/-/g, ' ');

export const DefaultGraphResult = ({
	icon,
	partial,
	position,
	resultAri,
	integrationKey,
}: GraphResultProps) => {
	const [primaryProduct] = usePrimaryProduct();
	const [{ cloudId }] = useBootstrap();

	const { lastModifiedDate, url, permissionLevel, title, excerpt } = partial;

	const lastModifiedComponent = lastModifiedDate ? (
		<LastModified lastModified={lastModifiedDate} />
	) : null;

	const label = getLabel(resultAri.resourceType);
	const isNotPublic = isNonPublic(permissionLevel);

	return (
		<SearchResult
			id={partial.id}
			description={processExcerpt(excerpt)}
			icon={icon}
			headerMetadata={
				<SearchMetadata
					productSelected
					product={integrationKey}
					parts={[
						['label', <Box xcss={typeLabelContainer}>{label}</Box>],
						['last-modified', lastModifiedComponent],
					]}
				/>
			}
			footer={
				isNotPublic && (
					<Box xcss={footerContainer}>
						<RestrictedLozenge restriction={permissionLevel} />
					</Box>
				)
			}
			title={enrichExcerpt(title)}
			url={url}
			isThirdParty
			cloudId={cloudId}
			primaryProduct={primaryProduct}
			position={position}
			partial={partial}
		/>
	);
};
